import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions-types/agent-quota-snapshot'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {
  INCREASE_INDIVIDUAL_BUDGET_URL,
  MANAGE_BUDGET_URL,
  UPGRADE_PLAN_URL_NEW,
} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useCallback, useSyncExternalStore} from 'react'

import {
  getQuotaBannerContent,
  getQuotaBannerDescription,
  getQuotaBannerState,
  isBlockingQuotaState,
  isHighSeverityApproachingLimit,
} from '../utils/agent-quota-notice-state'
import {
  EMPTY_RESULT,
  resolveQuotaBannerResult,
  type EntitlementSnapshot,
  type UseQuotaBannerResult,
} from '../utils/chat-quota-notice-state'

export interface EntitlementQuotaNotice {
  title: string
  description: string
  variant: 'critical' | 'warning' | 'info'
  primaryAction?: {label: string; href: string}
  secondaryAction?: {label: string; href: string}
  isDismissable?: boolean
  onDismiss?: () => void
}

export type EntitlementQuotaInput =
  | {kind: 'chat'; entitlement: EntitlementSnapshot | undefined}
  | {kind: 'agent'; snapshot: AgentQuotaSnapshot | undefined | null}

const DISMISSED_EVENT = 'copilot-quota-banner-dismissed'

function subscribeToDismissal(callback: () => void) {
  window.addEventListener(DISMISSED_EVENT, callback)
  return () => window.removeEventListener(DISMISSED_EVENT, callback)
}

export function useChatQuotaBannerResult(entitlement: EntitlementSnapshot | undefined): UseQuotaBannerResult {
  const result =
    entitlement && (entitlement.licenseProvisioning || entitlement.tokenBasedBillingEnabled)
      ? resolveQuotaBannerResult(entitlement)
      : EMPTY_RESULT
  const getSnapshot = useCallback(
    () => (result.variant ? copilotLocalStorage.getQuotaBannerFlag(result.variant) : null),
    [result.variant],
  )
  const isDismissed = useSyncExternalStore(subscribeToDismissal, getSnapshot, () => null)
  const handleDismiss = useCallback(() => {
    if (result.variant) {
      copilotLocalStorage.setQuotaBannerFlag(result.variant, true)
      window.dispatchEvent(new Event(DISMISSED_EVENT))
    }
  }, [result.variant])
  return {
    ...result,
    show: result.show && !isDismissed,
    onDismiss: result.isDismissable ? handleDismiss : undefined,
  }
}

function chatNotice(result: UseQuotaBannerResult): EntitlementQuotaNotice | null {
  if (!result.show || !result.variant) return null
  const {variant, resetLabel, quotaUsage} = result
  const resetSentence = resetLabel ? ` Limit resets ${resetLabel}.` : ''
  const used = Math.floor(quotaUsage ?? 75)
  const upgrade = {label: 'Upgrade plan', href: UPGRADE_PLAN_URL_NEW}
  const budget = (label: string) => ({label, href: MANAGE_BUDGET_URL})
  const exceededBudget = {label: 'Increase your budget', href: INCREASE_INDIVIDUAL_BUDGET_URL}
  const individualBudget =
    result.showIncreaseBudgetButton &&
    copilotFeatureFlags.ipmUbbIndividualBudgetBanner &&
    (variant === 'paidExceeded' || variant === 'overageExceeded')
  let title: string
  let description: string
  let severity: EntitlementQuotaNotice['variant']
  let budgetLabel: string | undefined
  let canUpgrade = false
  switch (variant) {
    case 'freeApproaching':
      title = 'Approaching limit'
      description = "You're approaching the limit of your included usage with the free plan."
      severity = 'info'
      canUpgrade = true
      break
    case 'freeAlmostOut':
    case 'freeExceeded':
      title = variant === 'freeExceeded' ? 'Limit reached' : 'Almost out'
      description = `You're ${variant === 'freeExceeded' ? 'out of' : 'almost out of'} your included usage with the free plan.${resetSentence}`
      severity = variant === 'freeExceeded' ? 'critical' : 'warning'
      canUpgrade = true
      break
    case 'paidApproaching':
    case 'paidAlmostOut':
      title = variant === 'paidAlmostOut' ? 'Almost out' : 'Approaching limit'
      description = `You've used ${used}% of your included AI credits.${resetSentence}`
      severity = variant === 'paidAlmostOut' ? 'warning' : 'info'
      budgetLabel = 'Enable additional usage'
      canUpgrade = true
      break
    case 'paidExceeded':
      title = 'Limit reached'
      description = `You've run out of your included AI credits for the month.${resetSentence}`
      severity = 'critical'
      budgetLabel = 'Enable additional usage'
      canUpgrade = true
      break
    case 'overageApproaching':
    case 'overageAlmostOut':
      title = variant === 'overageAlmostOut' ? 'Almost out of budget' : 'Approaching budget limit'
      description = `You've used ${used}% of your budget for additional AI credits.`
      severity = variant === 'overageAlmostOut' ? 'warning' : 'info'
      budgetLabel = 'Increase budget'
      canUpgrade = true
      break
    case 'overageExceeded':
      title = 'Budget reached'
      description = "You've run out of your budget for additional AI credits."
      severity = 'critical'
      budgetLabel = 'Increase budget'
      canUpgrade = true
      break
    case 'overageActive':
      title = 'Additional usage active'
      description = `You're now using additional usage until your included credits reset${resetLabel ? ` ${resetLabel}` : ''}.`
      severity = 'info'
      budgetLabel = 'Manage overage'
      canUpgrade = true
      break
    case 'userLevelBudgetProgress':
    case 'userLevelBudgetAlmostReached':
      title = variant === 'userLevelBudgetProgress' ? 'Approaching limit' : 'Almost at limit'
      description = `You have used ${used}% of your monthly usage limit.`
      severity = variant === 'userLevelBudgetProgress' ? 'info' : 'warning'
      break
    case 'userLevelBudgetReached':
      title = 'Usage paused'
      description = `Usage paused until your monthly limit resets${resetLabel ? ` ${resetLabel}` : ''}. Request your administrator for additional usage.`
      severity = 'critical'
      break
    case 'userLevelBudgetUsageBlocked':
      title = 'Usage blocked'
      description = 'Usage is currently blocked by your organization. Contact your administrator to resume usage.'
      severity = 'critical'
      break
    case 'licenseProvisioning':
      title = 'Setting up your Copilot license'
      description = 'Cleared for takeoff soon—your Copilot license is on its way. Please check back in a few minutes.'
      severity = 'info'
      break
    default:
      return null
  }
  const primaryAction = individualBudget
    ? exceededBudget
    : result.showOverageButton && budgetLabel
      ? budget(budgetLabel)
      : canUpgrade && result.showUpgradeButton
        ? upgrade
        : undefined
  const secondaryAction =
    !individualBudget && primaryAction?.href === MANAGE_BUDGET_URL && canUpgrade && result.showUpgradeButton
      ? upgrade
      : undefined
  return {
    title,
    description,
    variant: severity,
    primaryAction,
    secondaryAction,
    isDismissable: result.isDismissable,
    onDismiss: result.onDismiss,
  }
}

export function getAgentQuotaNotice(snapshot: AgentQuotaSnapshot | undefined | null): {
  notice: EntitlementQuotaNotice | null
  isBlocking: boolean
} {
  const state = getQuotaBannerState(snapshot)
  if (state.kind === 'none') return {notice: null, isBlocking: false}
  const content = getQuotaBannerContent(state, snapshot)
  const individualBudget =
    snapshot?.is_paid_individual === true &&
    copilotFeatureFlags.ipmUbbIndividualBudgetBanner &&
    (state.kind === 'cfiBlocked' || state.kind === 'cfiBudgetHit')
  const primaryAction = individualBudget
    ? {label: 'Increase your budget', href: INCREASE_INDIVIDUAL_BUDGET_URL}
    : (content.primaryAction ?? content.upgradeAction)
  return {
    notice: {
      title: content.title,
      description: getQuotaBannerDescription(state),
      variant: isBlockingQuotaState(state)
        ? 'critical'
        : state.kind === 'approachingLimit' && isHighSeverityApproachingLimit(state.percentUsed)
          ? 'warning'
          : 'info',
      primaryAction,
      secondaryAction: content.primaryAction ? content.upgradeAction : undefined,
    },
    isBlocking: isBlockingQuotaState(state),
  }
}

export function useEntitlementQuotaNotice(input: EntitlementQuotaInput): {
  notice: EntitlementQuotaNotice | null
  isBlocking: boolean
} {
  const chatResult = useChatQuotaBannerResult(input.kind === 'chat' ? input.entitlement : undefined)
  if (input.kind === 'agent') return getAgentQuotaNotice(input.snapshot)
  const legacyChatQuotaBlocked =
    input.entitlement?.tokenBasedBillingEnabled !== true &&
    input.entitlement?.licenseProvisioning !== true &&
    input.entitlement?.chatQuotaExceeded === true
  const legacyNotice: EntitlementQuotaNotice | null = legacyChatQuotaBlocked
    ? {
        title: 'Limit reached',
        description: `You have reached your free plan limit.${
          input.entitlement?.resetDate ? ` Limit resets on ${input.entitlement.resetDate}.` : ''
        }`,
        variant: 'critical',
      }
    : null
  return {notice: chatNotice(chatResult) ?? legacyNotice, isBlocking: chatResult.isBlocking || legacyChatQuotaBlocked}
}
